import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import '../styles/PartsCalculator.css';

const PartsCalculator = () => {
  const { user } = useAuth();
  
  // Data states
  const [parts, setParts] = useState([]);
  const [priceLists, setPriceLists] = useState([]);
  const [settings, setSettings] = useState({ laborCostPerHour: 150 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Selection states
  const [selectedParts, setSelectedParts] = useState({});
  const [selectedPriceList, setSelectedPriceList] = useState(null);
  
  // Sale & Rental states
  const [isSale, setIsSale] = useState(true);
  const [isRental, setIsRental] = useState(false);
  const [rentalYears, setRentalYears] = useState(5);
  const [interestRate, setInterestRate] = useState(7);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [partsRes, priceListsRes, settingsRes] = await Promise.all([
        api.get('/parts'),
        api.get('/price-lists'),
        api.get('/settings')
      ]);
      
      // Filter only parts with showInCalculator = true
      const calculatorParts = partsRes.data.filter(p => p.showInCalculator);
      setParts(calculatorParts);
      setPriceLists(priceListsRes.data);
      setSettings(settingsRes.data);
      
      if (priceListsRes.data.length > 0) {
        const minLevel = user?.minPriceList || 1;
        const defaultPriceList = priceListsRes.data.find(p => p.level >= minLevel) || priceListsRes.data[0];
        setSelectedPriceList(defaultPriceList);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePartToggle = (part) => {
    const partId = part._id;
    
    setSelectedParts(prev => {
      if (prev[partId]) {
        const newSelected = { ...prev };
        delete newSelected[partId];
        return newSelected;
      } else {
        return {
          ...prev,
          [partId]: { part, quantity: 1 }
        };
      }
    });
  };

  const handleQuantityChange = (partId, quantity) => {
    setSelectedParts(prev => ({
      ...prev,
      [partId]: { ...prev[partId], quantity: Math.max(1, quantity) }
    }));
  };

  const calculateTotalCost = () => {
    let total = 0;
    Object.values(selectedParts).forEach(({ part, quantity }) => {
      total += (part.cost || 0) * quantity;
    });
    return total;
  };

  const calculateSellingPrice = () => {
    const cost = calculateTotalCost();
    const margin = selectedPriceList?.marginPercent || 0;
    return cost * (1 + margin / 100);
  };

  const calculateProfit = () => {
    return calculateSellingPrice() - calculateTotalCost();
  };

  const calculateProfitPercent = () => {
    const cost = calculateTotalCost();
    if (cost === 0) return 0;
    return (calculateProfit() / cost) * 100;
  };

  // PMT formula: r*P / (1 - (1+r)^-n)
  const calculateMonthlyRental = () => {
    const P = calculateSellingPrice();
    const annualRate = interestRate / 100;
    const r = annualRate / 12;
    const n = rentalYears * 12;
    
    if (r === 0) return P / n;
    
    return (r * P) / (1 - Math.pow(1 + r, -n));
  };

  const calculateTotalRental = () => {
    return calculateMonthlyRental() * rentalYears * 12;
  };

  const calculateRentalInterest = () => {
    return calculateTotalRental() - calculateSellingPrice();
  };

  const handleReset = () => {
    setSelectedParts({});
    setIsSale(true);
    setIsRental(false);
    setRentalYears(5);
    setInterestRate(7);
  };

  const filteredParts = parts.filter(part =>
    part.sku?.includes(search) ||
    part.nameHe?.includes(search) ||
    (part.nameEn && part.nameEn.toLowerCase().includes(search.toLowerCase()))
  );

  if (loading) {
    return <div className="loading">טוען...</div>;
  }

  return (
    <div className="parts-calculator">
      <div className="page-header">
        <h1>מחשבון פריטים</h1>
        <button className="btn-reset" onClick={handleReset}>איפוס</button>
      </div>

      <div className="calculator-layout">
        {/* Parts Selection */}
        <div className="parts-section">
          <div className="search-box">
            <input
              type="text"
              placeholder="חיפוש פריט לפי מק״ט או שם..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {filteredParts.length === 0 ? (
            <div className="no-data">
              אין פריטים להצגה במחשבון.
              <br />
              יש לסמן פריטים ב"הצג במחשבון פריטים" בעמוד עריכת פריטים.
            </div>
          ) : (
            <div className="parts-list-container">
              {filteredParts.map(part => {
                const isSelected = !!selectedParts[part._id];
                const partData = selectedParts[part._id];
                
                return (
                  <div 
                    key={part._id} 
                    className={`part-item ${isSelected ? 'selected' : ''}`}
                  >
                    <label className="part-checkbox">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handlePartToggle(part)}
                      />
                      <div className="part-info">
                        <span className="part-name">{part.nameHe}</span>
                        <span className="part-sku">{part.sku}</span>
                      </div>
                    </label>
                    
                    {isSelected && (
                      <input
                        type="number"
                        min="1"
                        value={partData?.quantity || 1}
                        onChange={(e) => handleQuantityChange(part._id, parseInt(e.target.value) || 1)}
                        className="quantity-input"
                      />
                    )}
                    
                    {user?.role !== 'sales' && (
                      <span className="part-cost">
                        ₪{part.cost?.toFixed(2) || 0}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Settings & Results */}
        <div className="settings-section">
          {/* Price List Selection */}
          <div className="setting-card">
            <label>מחירון:</label>
            <select
              value={selectedPriceList?._id || ''}
              onChange={(e) => {
                const pl = priceLists.find(p => p._id === e.target.value);
                setSelectedPriceList(pl);
              }}
            >
              {priceLists.map(pl => (
                <option key={pl._id} value={pl._id}>
                  {pl.name}{user?.role !== 'sales' ? ` (${pl.marginPercent}%)` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Sale & Rental Options */}
          <div className="setting-card pricing-options-card">
            <div className="pricing-checkboxes">
              <label className="pricing-checkbox">
                <input
                  type="checkbox"
                  checked={isSale}
                  onChange={(e) => setIsSale(e.target.checked)}
                />
                מכירה
              </label>
              
              <label className="pricing-checkbox">
                <input
                  type="checkbox"
                  checked={isRental}
                  onChange={(e) => setIsRental(e.target.checked)}
                />
                השכרה
              </label>
            </div>
            
            {isRental && (
              <div className="rental-options">
                <div className="rental-field">
                  <label>שנים:</label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={rentalYears}
                    onChange={(e) => setRentalYears(parseInt(e.target.value) || 1)}
                  />
                </div>
                <div className="rental-field">
                  <label>ריבית (%):</label>
                  <input
                    type="number"
                    min="0"
                    step="0.125"
                    value={interestRate}
                    onChange={(e) => setInterestRate(parseFloat(e.target.value) || 0)}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Results */}
          <div className="results-card">
            <h3>סיכום</h3>
            
            {user?.role !== 'sales' && (
              <div className="result-row">
                <span>מחיר עלות:</span>
                <strong>₪{calculateTotalCost().toFixed(2)}</strong>
              </div>
            )}
            
            {isSale && (
              <div className="sale-results">
                <h4>מכירה</h4>
                <div className="result-row highlight">
                  <span>מחיר מכירה:</span>
                  <strong>₪{calculateSellingPrice().toFixed(2)}</strong>
                </div>
                
                {user?.role !== 'sales' && (
                  <>
                    <div className="result-row">
                      <span>רווח:</span>
                      <strong className="profit">₪{calculateProfit().toFixed(2)}</strong>
                    </div>
                    <div className="result-row">
                      <span>אחוז רווח:</span>
                      <strong className="profit">{calculateProfitPercent().toFixed(1)}%</strong>
                    </div>
                  </>
                )}
              </div>
            )}
            
            {isRental && (
              <div className="rental-results">
                <h4>השכרה ({rentalYears} שנים)</h4>
                <div className="result-row highlight">
                  <span>תשלום חודשי:</span>
                  <strong>₪{calculateMonthlyRental().toFixed(2)}</strong>
                </div>
                <div className="result-row">
                  <span>סה״כ לתקופה:</span>
                  <strong>₪{calculateTotalRental().toFixed(2)}</strong>
                </div>
                {user?.role !== 'sales' && (
                  <div className="result-row">
                    <span>סה״כ ריבית:</span>
                    <strong>₪{calculateRentalInterest().toFixed(2)}</strong>
                  </div>
                )}
              </div>
            )}
            
            {!isSale && !isRental && (
              <div className="no-selection">
                בחר מכירה או השכרה
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartsCalculator;